import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Spinner, Alert, Modal } from 'react-bootstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {
  ArrowLeft, Edit3, Plus, Trash2, Users, DollarSign,
  UserPlus, RefreshCw, Calendar, Loader2,
  AlertTriangle, ChevronDown, ChevronUp, Check, X,
    Briefcase
} from 'lucide-react';
import PageHeader from '../../../layouts/PageHeader';
import * as api from '../../../../services/api/projectsApi';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as S from '../../Common/internalToolsStyles';
import { format } from 'date-fns';
import Chart from 'react-apexcharts';
import { useCapabilities } from '../../../../context/CapabilityContext';

const roleLabels = { member: 'Medlem', lead: 'Ledare', manager: 'Ansvarig', viewer: 'Läsare' };
const roleBadge = {
  member: S.badge(S.colors.textMuted, 'rgba(107,114,128,0.08)'),
  lead: S.badge(S.colors.info, S.colors.infoLight),
  manager: S.badge('var(--primary)', 'var(--rgba-primary-1)'),
  viewer: S.badge(S.colors.textLight, 'rgba(156,163,175,0.08)'),
};

const fmtCurrency = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const ProjectDetailsPage = () => {
  const { companyId, projectId } = useParams();
  const history = useHistory();

  // Capability×scope gating per §4.4. The page is readable by anyone the
  // backend admitted via assertCanReadProject (admin tier ∨ member ∨ open
  // project), so we don't gate the whole route — just the management
  // affordances. `canManage` is true when caller is Admin/Owner OR the
  // authoritative manager of this specific project (project_member.
  // project_role='manager'). Fail-closed during the initial capabilities
  // fetch so buttons don't flash for non-managers on first paint.
  const { capabilities } = useCapabilities();
  const canManage = useMemo(() => {
    if (!capabilities) return false;
    if (capabilities.isAdmin || capabilities.isOwner) return true;
    return (capabilities.managedProjectIds || []).includes(projectId);
  }, [capabilities, projectId]);

  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [rateCardLines, setRateCardLines] = useState([]);
  const [stats, setStats] = useState(null);
  const [weeklyTrend, setWeeklyTrend] = useState([]);
  const [memberBreakdown, setMemberBreakdown] = useState([]);
  const [taskBreakdown, setTaskBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [newMember, setNewMember] = useState({ employeeId: '', projectRole: 'member' });

  const [showAddRate, setShowAddRate] = useState(false);
  const [newRate, setNewRate] = useState({ billingRate: '', positionId: '', employeeId: '', articleId: '', newArticleName: '' });
  const [articles, setArticles] = useState([]);

  // Inline-confirm states (two-click pattern replaces swal)
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null); // member_id
  const [confirmRemoveRate, setConfirmRemoveRate] = useState(null);     // line_id

  // Charts are heavy and partly duplicate the stats strip; hide by default.
  const [showCharts, setShowCharts] = useState(false);

  // Dismiss the setup banner per-session; don't persist — a user who
  // returns later probably still wants the reminder if fields are missing.
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [projRes, membersRes, statsRes] = await Promise.all([
        api.getProject(companyId, projectId),
        api.getProjectMembers(companyId, projectId),
        api.getProjectStats(companyId, projectId).catch(() => ({ data: { summary: {}, weeklyTrend: [], memberBreakdown: [] } })),
      ]);
      const proj = projRes.data.project;
      setProject(proj);
      setMembers(membersRes.data.members || []);
      setStats(statsRes.data.summary || {});
      setWeeklyTrend(statsRes.data.weeklyTrend || []);
      setMemberBreakdown(statsRes.data.memberBreakdown || []);
      setTaskBreakdown(statsRes.data.taskBreakdown || []);
      if (proj?.rate_card_id) {
        try {
          const rcRes = await api.getRateCard(companyId, proj.rate_card_id);
          setRateCardLines(rcRes.data.rateCard?.lines || []);
        } catch (e) { setRateCardLines([]); }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda projekt');
    } finally {
      setLoading(false);
    }
  }, [companyId, projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (showAddMember && employees.length === 0) {
      axios.get(getApiUrl(`${companyId}/hr/employees`), { withCredentials: true })
        .then(res => setEmployees(res.data.activeEmployees || res.data.data?.activeEmployees || []))
        .catch(() => {});
    }
  }, [showAddMember, companyId, employees.length]);

  useEffect(() => {
    if (showAddRate && articles.length === 0) {
      axios.get(getApiUrl(`${companyId}/articles`), { withCredentials: true })
        .then(res => {
          const d = Array.isArray(res.data) ? res.data : res.data?.data || [];
          setArticles(d.filter(a => a.type === 'Service' || a.unit === 'Hour'));
        })
        .catch(() => setArticles([]));
    }
  }, [showAddRate, companyId, articles.length]);

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await api.updateProject(companyId, projectId, { isActive: false });
      history.push(`/${companyId}/projects`);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte inaktivera projektet');
      setDeactivating(false);
      setConfirmDeactivate(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.employeeId) return;
    try {
      await api.addProjectMember(companyId, projectId, newMember);
      setShowAddMember(false);
      setNewMember({ employeeId: '', projectRole: 'member' });
      await loadData();
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte lägga till medlem'); }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await api.removeProjectMember(companyId, memberId);
      setConfirmRemoveMember(null);
      await loadData();
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte ta bort'); }
  };

  const handleAddRateLine = async () => {
    if (!newRate.billingRate) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      let rateCardId = project?.rate_card_id;
      if (!rateCardId) {
        await api.updateProject(companyId, projectId, { hourlyRate: parseFloat(newRate.billingRate) });
        const projRes = await api.getProject(companyId, projectId);
        rateCardId = projRes.data.project?.rate_card_id;
        if (!rateCardId) { setError('Kunde inte skapa prislista'); return; }
        if (!newRate.employeeId && !newRate.positionId) {
          setShowAddRate(false); setNewRate({ billingRate: '', positionId: '', employeeId: '', articleId: '', newArticleName: '' });
          await loadData(); return;
        }
      }
      let articleId = newRate.articleId || null;
      if (newRate.newArticleName && !articleId) {
        const artRes = await axios.post(getApiUrl(`${companyId}/articles`), { description: newRate.newArticleName, type: 'Service', unit: 'Hour', unitprice: parseFloat(newRate.billingRate), tax_rate: '25%' }, { withCredentials: true });
        articleId = artRes.data?.data?.article_id || artRes.data?.articleId || artRes.data?.article_id || null;
      }
      await axios.post(getApiUrl(`${companyId}/rate-cards/${rateCardId}/lines`), { billingRate: parseFloat(newRate.billingRate), employeeId: newRate.employeeId || null, positionId: newRate.positionId || null, articleId, validFrom: today }, { withCredentials: true });
      setShowAddRate(false); setNewRate({ billingRate: '', positionId: '', employeeId: '', articleId: '', newArticleName: '' });
      await loadData();
    } catch (err) { setError(err.response?.data?.message || 'Kunde inte lägga till uppgift'); }
  };

  const handleRemoveRateLine = async (lineId) => {
    try {
      await axios.delete(getApiUrl(`${companyId}/rate-card-lines/${lineId}`), { withCredentials: true });
      setConfirmRemoveRate(null);
      await loadData();
    }
    catch (err) { setError(err.response?.data?.message || 'Kunde inte ta bort'); }
  };

  const rateRange = useMemo(() => {
    if (!rateCardLines.length) return null;
    const rates = rateCardLines.map(l => Number(l.billing_rate)).filter(r => r > 0);
    if (!rates.length) return null;
    const min = Math.min(...rates);
    const max = Math.max(...rates);
    return { min, max, single: min === max };
  }, [rateCardLines]);

  const budgetPct = useMemo(() => {
    if (!project?.budget_hours || !stats?.approved_minutes) return null;
    return ((stats.approved_minutes / 60) / project.budget_hours) * 100;
  }, [project, stats]);

  const burnUpData = useMemo(() => {
    if (!weeklyTrend.length) return { actual: [], budget: null };
    let cumH = 0;
    const actual = weeklyTrend.map(w => {
      cumH += Number(w.hours) || 0;
      return { x: w.week_start, y: Math.round(cumH * 10) / 10 };
    });
    return { actual, budget: project?.budget_hours || null };
  }, [weeklyTrend, project?.budget_hours]);

  // Mirrors the stub-detection in ProjectsPage + ProjectEditPage so the
  // user sees a consistent "what's missing" story across the feature.
  const missingFields = useMemo(() => {
    if (!project) return [];
    const m = [];
    if (project.billing_model === 'time_materials' && !project.hourly_rate) m.push('Timpris');
    if (project.project_type === 'client' && !project.customer_id) m.push('Kund');
    if (!project.code) m.push('Projektkod');
    if (!project.budget_hours && project.billing_model !== 'non_billable') m.push('Budget');
    return m;
  }, [project]);

  if (loading) return <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}><Spinner animation="border" variant="primary" /></div>;
  if (!project) return <Alert variant="danger">Projektet hittades inte.</Alert>;

  const billingStyle = S.billingBadge[project.billing_model] || S.billingBadge.non_billable;
  const billingLabel = S.billingLabel[project.billing_model] || S.billingLabel.non_billable;
  const existingMemberIds = members.map(m => m.employee_id);
  const availableEmployees = employees.filter(e => !existingMemberIds.includes(e.employee_id || e.id));
  const totalH = stats?.total_minutes ? (stats.total_minutes / 60) : 0;
  const approvedH = stats?.approved_minutes ? (stats.approved_minutes / 60) : 0;
  const pendingH = Math.max(0, totalH - approvedH);
  const billableValue = stats?.billable_value || 0;
  const invoicedValue = stats?.invoiced_value || 0;
  const effectiveRate = approvedH > 0 && billableValue > 0 ? billableValue / approvedH : null;
  const budgetRemaining = project?.budget_hours ? project.budget_hours - approvedH : null;
  const budgetOverrun = budgetPct !== null && budgetPct > 100;

  const taskColors = [S.colors.primary, S.colors.info, S.colors.warning, '#7c3aed', S.colors.success, '#ec4899', '#f97316', '#06b6d4'];

  // Thin stats strip content — Rapporterat · Debiterat · Effektiv timpris · Medlemmar
  const stripItems = [
    {
      label: 'Godkänt',
      value: `${approvedH.toFixed(approvedH % 1 ? 1 : 0)}h`,
      color: S.colors.textPrimary,
      sub: project.budget_hours
        ? `av ${project.budget_hours}h${budgetOverrun ? ` · +${((budgetPct || 0) - 100).toFixed(0)}% över` : budgetRemaining !== null ? ` · ${budgetRemaining.toFixed(0)}h kvar` : ''}${pendingH > 0 ? ` · ${pendingH.toFixed(pendingH % 1 ? 1 : 0)}h ej godkänt` : ''}`
        : pendingH > 0 ? `${pendingH.toFixed(pendingH % 1 ? 1 : 0)}h ej godkänt` : null,
    },
    {
      label: 'Debiterat',
      value: fmtCurrency(billableValue),
      color: S.colors.success,
      sub: invoicedValue > 0 ? `${fmtCurrency(invoicedValue)} fakturerat` : 'ej fakturerat',
    },
    {
      label: 'Effektiv timpris',
      value: effectiveRate ? `${fmtCurrency(effectiveRate)} ${project.currency}/h` : '–',
      color: effectiveRate ? S.colors.warning : S.colors.textLight,
    },
    {
      label: 'Medlemmar',
      value: String(members.length),
      color: S.colors.info,
      sub: stats?.unique_employees > 0 && stats.unique_employees !== members.length
        ? `${stats.unique_employees} har rapporterat` : null,
    },
  ];

  return (
    <>
      <PageHeader
        backTo={{ to: `/${companyId}/projects`, label: 'Alla projekt' }}
        icon={Briefcase}
        title={project.name}
      />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ marginBottom: 12 }}>
          {error}
        </Alert>
      )}

      {/* Sticky toolbar — back, title block (name + code + billing), actions. */}
      <div className="proj-toolbar proj-detail-toolbar">
        <button
          type="button"
          onClick={() => history.push(`/${companyId}/projects`)}
          style={S.btnSecondary}
        >
          <ArrowLeft size={14} /> Tillbaka
        </button>
        <div className="proj-detail-titleblock">
          <div className="proj-detail-title-row">
            <span
              className="proj-color-dot"
              style={{ backgroundColor: project.color || S.colors.textMuted }}
              aria-hidden="true"
            />
            <span className="proj-detail-title">{project.name}</span>
            {project.code && <span className="proj-detail-code">{project.code}</span>}
            <span style={billingStyle}>{billingLabel}</span>
          </div>
          {(project.customer_name || project.client_reference) && (
            <div className="proj-detail-sub">
              {project.customer_name && <span>{project.customer_name}</span>}
              {project.client_reference && (
                <>
                  <span className="proj-detail-sep">·</span>
                  <span>Ref: {project.client_reference}</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className="proj-toolbar-right">
          <button
            type="button"
            onClick={loadData}
            style={S.btnIcon}
            title="Uppdatera"
          >
            <RefreshCw size={14} />
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => history.push(`/${companyId}/projects/${projectId}/edit`)}
              style={S.btnPrimary}
            >
              <Edit3 size={14} /> Redigera
            </button>
          )}
          {canManage && (confirmDeactivate ? (
            <div className="proj-inline-confirm">
              <span className="proj-inline-confirm-label">Säker?</span>
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={deactivating}
                className="proj-inline-confirm-yes"
                title="Bekräfta inaktivering"
              >
                {deactivating ? <Loader2 size={14} className="proj-spin" /> : <Check size={14} />}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDeactivate(false)}
                disabled={deactivating}
                className="proj-inline-confirm-no"
                title="Avbryt"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDeactivate(true)}
              style={S.btnDanger}
              title="Inaktivera projekt"
            >
              <Trash2 size={14} />
            </button>
          ))}
        </div>
      </div>

      {/* Ofullständig banner — surfaces what's missing on stub projects.
          Mirrors ProjectEditPage's setup banner so the quick-create →
          detail-page handoff feels continuous. */}
      {missingFields.length > 0 && !bannerDismissed && (
        <div className="proj-setup-banner">
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <AlertTriangle size={16} style={{ color: '#92400e', flexShrink: 0, marginTop: 1 }} />
            <div>
              <div className="proj-setup-banner-title">Projektet saknar uppgifter</div>
              <div className="proj-setup-banner-desc">
                Fyll i <b>{missingFields.join(', ')}</b> så att projektet är redo för tidrapportering och fakturering.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => history.push(`/${companyId}/projects/${projectId}/edit?setup=1`)}
              style={{ ...S.btnPrimary, padding: '6px 12px', fontSize: 12 }}
            >
              Komplettera
            </button>
            <button
              type="button"
              onClick={() => setBannerDismissed(true)}
              className="proj-setup-skip"
            >
              Göm
            </button>
          </div>
        </div>
      )}

      {/* Description + period — slim meta line under the toolbar. */}
      {(project.description || stats?.first_entry) && (
        <div className="proj-detail-meta">
          {project.description && <div className="proj-detail-desc">{project.description}</div>}
          {stats?.first_entry && (
            <div className="proj-detail-period">
              <Calendar size={12} />
              {format(new Date(stats.first_entry), 'd MMM yyyy')} – {stats.last_entry ? format(new Date(stats.last_entry), 'd MMM yyyy') : 'pågående'}
              {rateRange && (
                <>
                  <span className="proj-detail-sep">·</span>
                  <span>
                    {rateRange.single ? fmtCurrency(rateRange.min) : `${fmtCurrency(rateRange.min)}–${fmtCurrency(rateRange.max)}`} {project.currency}/h
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Thin stats strip — replaces the 4-tile stat-card grid. */}
      <div className="proj-stats-strip" style={{ marginBottom: 12 }}>
        {stripItems.map((s, i) => (
          <React.Fragment key={s.label}>
            {i > 0 && <span className="proj-stat-sep">·</span>}
            <span className="proj-stat">
              <span className="proj-stat-label">{s.label}:</span>
              <b style={{ color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.value}</b>
              {s.sub && <span className="proj-stat-sub">{s.sub}</span>}
            </span>
          </React.Fragment>
        ))}
      </div>

      {/* Budget progress bar — shown when there's a budget. Replaces the
          tile-internal bar with a single page-wide signal. */}
      {budgetPct !== null && (
        <div className="proj-budget-bar" style={{ marginBottom: 12 }}>
          <div
            className="proj-budget-bar-fill"
            style={{
              width: `${Math.min(budgetPct, 100)}%`,
              background: budgetOverrun ? S.colors.danger : budgetPct > 75 ? S.colors.warning : 'var(--primary)',
            }}
          />
          {budgetOverrun && (
            <div
              className="proj-budget-bar-overrun"
              style={{ width: `${Math.min(budgetPct - 100, 30)}%` }}
            />
          )}
        </div>
      )}

      {/* Charts — heavy visualization collapsed by default. The strip above
          carries the headline numbers; detailed viz lives on the reports
          page. */}
      {(burnUpData.actual.length > 1 || memberBreakdown.length > 0 || taskBreakdown.length > 0) && (
        <div style={{ marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => setShowCharts(v => !v)}
            className="proj-disclosure"
          >
            {showCharts ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showCharts ? 'Göm diagram' : 'Visa diagram'}
          </button>
          {showCharts && (
            <div className="proj-charts" style={{ marginTop: 12 }}>
              {burnUpData.actual.length > 1 && (
                <div style={{ ...S.card, padding: '16px 20px', marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={S.text.title}>
                      {burnUpData.budget ? 'Budgetförbrukning' : 'Kumulativa timmar'}
                    </div>
                    {burnUpData.budget && (
                      <span style={{ fontSize: 11, color: S.colors.textLight }}>Budget: {burnUpData.budget}h</span>
                    )}
                  </div>
                  <Chart
                    type="area"
                    height={240}
                    series={[{ name: 'Timmar', data: burnUpData.actual }]}
                    options={{
                      chart: { toolbar: { show: false }, fontFamily: 'inherit' },
                      colors: ['var(--primary)'],
                      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 95, 100] } },
                      stroke: { curve: 'smooth', width: 2.5 },
                      xaxis: {
                        type: 'datetime',
                        labels: { style: { fontSize: '10px', colors: S.colors.textDisabled }, datetimeFormatter: { month: 'MMM', week: 'd MMM' } },
                        axisBorder: { show: false }, axisTicks: { show: false },
                      },
                      yaxis: {
                        max: burnUpData.budget ? Math.max(burnUpData.budget * 1.1, burnUpData.actual[burnUpData.actual.length - 1]?.y * 1.1) : undefined,
                        labels: { style: { fontSize: '10px', colors: S.colors.textDisabled }, formatter: v => `${Math.round(v)}h` },
                      },
                      annotations: burnUpData.budget ? {
                        yaxis: [{
                          y: burnUpData.budget,
                          borderColor: S.colors.danger,
                          strokeDashArray: 6,
                          label: {
                            text: `Budget ${burnUpData.budget}h`,
                            position: 'left',
                            style: { fontSize: '10px', color: S.colors.danger, background: S.colors.dangerLight, padding: { left: 6, right: 6, top: 2, bottom: 2 } },
                          },
                        }],
                      } : {},
                      grid: { borderColor: S.colors.borderLight, strokeDashArray: 4, padding: { left: 8, right: 8 } },
                      dataLabels: { enabled: false },
                      tooltip: { y: { formatter: v => `${v}h` }, x: { format: 'd MMM yyyy' } },
                    }}
                  />
                </div>
              )}

              {memberBreakdown.length > 0 && (
                <div style={{ ...S.card, padding: '16px 20px', marginBottom: 12 }}>
                  <div style={{ ...S.text.title, marginBottom: 4 }}>Timmar per person</div>
                  <Chart
                    type="bar"
                    height={Math.max(100, memberBreakdown.length * 36 + 30)}
                    series={[{ name: 'Timmar', data: memberBreakdown.map(m => Math.round(m.hours * 10) / 10) }]}
                    options={{
                      chart: { toolbar: { show: false }, fontFamily: 'inherit' },
                      colors: ['var(--primary)'],
                      plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '55%' } },
                      xaxis: {
                        labels: { style: { fontSize: '10px', colors: S.colors.textDisabled }, formatter: v => `${v}h` },
                        axisBorder: { show: false }, axisTicks: { show: false },
                      },
                      yaxis: { labels: { style: { fontSize: '11px', colors: S.colors.textSecondary } } },
                      labels: memberBreakdown.map(m => `${m.firstname} ${(m.lastname || '')[0] || ''}.`),
                      grid: { borderColor: S.colors.borderLight, strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
                      dataLabels: { enabled: true, formatter: v => `${v}h`, style: { fontSize: '10px', fontWeight: 600 }, offsetX: 4 },
                      tooltip: { y: { formatter: v => `${v}h` } },
                    }}
                  />
                </div>
              )}

              {taskBreakdown.length > 0 && (
                <div style={{ ...S.card, padding: '16px 20px' }}>
                  <div style={{ ...S.text.title, marginBottom: 12 }}>Fördelning per uppgift</div>
                  <div className="proj-charts-donuts">
                    <div>
                      <div style={{ ...S.text.caption, textAlign: 'center', marginBottom: 4 }}>Timmar</div>
                      <Chart
                        type="donut"
                        height={170}
                        series={taskBreakdown.map(t => Math.round((Number(t.hours) || 0) * 10) / 10)}
                        options={{
                          chart: { fontFamily: 'inherit' },
                          colors: taskColors.slice(0, taskBreakdown.length),
                          labels: taskBreakdown.map(t => t.task_name || 'Standard'),
                          plotOptions: {
                            pie: {
                              expandOnClick: true,
                              donut: {
                                size: '72%',
                                labels: {
                                  show: true,
                                  name: { show: false },
                                  total: { show: true, label: '', fontSize: '18px', fontWeight: 700, color: S.colors.textPrimary, formatter: () => `${totalH.toFixed(totalH % 1 ? 1 : 0)}h` },
                                  value: { show: true, fontSize: '18px', fontWeight: 700, color: S.colors.textPrimary, offsetY: 6, formatter: v => `${v}h` },
                                },
                              },
                            },
                          },
                          stroke: { width: 2, colors: ['#fff'] },
                          dataLabels: { enabled: false },
                          legend: { show: false },
                          tooltip: { y: { formatter: v => `${v}h` } },
                        }}
                      />
                    </div>
                    <div>
                      <div style={{ ...S.text.caption, textAlign: 'center', marginBottom: 4 }}>Intäkter</div>
                      <Chart
                        type="donut"
                        height={170}
                        series={taskBreakdown.map(t => Math.round(Number(t.value) || 0))}
                        options={{
                          chart: { fontFamily: 'inherit' },
                          colors: taskColors.slice(0, taskBreakdown.length),
                          labels: taskBreakdown.map(t => t.task_name || 'Standard'),
                          plotOptions: {
                            pie: {
                              expandOnClick: true,
                              donut: {
                                size: '72%',
                                labels: {
                                  show: true,
                                  name: { show: false },
                                  total: { show: true, label: '', fontSize: '16px', fontWeight: 700, color: S.colors.textPrimary, formatter: () => `${fmtCurrency(billableValue)}` },
                                  value: { show: true, fontSize: '16px', fontWeight: 700, color: S.colors.textPrimary, offsetY: 6, formatter: v => fmtCurrency(v) },
                                },
                              },
                            },
                          },
                          stroke: { width: 2, colors: ['#fff'] },
                          dataLabels: { enabled: false },
                          legend: { show: false },
                          tooltip: { y: { formatter: v => `${fmtCurrency(v)} ${project.currency}` } },
                        }}
                      />
                    </div>
                  </div>
                  {/* Shared legend */}
                  <div className="proj-donut-legend">
                    {taskBreakdown.map((t, i) => (
                      <div key={i} className="proj-donut-legend-item">
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: taskColors[i % taskColors.length] }} />
                        {t.task_name || 'Standard'}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Content split — pricing left, members right. CSS grid, not Row/Col. */}
      <div className="proj-detail-split">
        {/* Pricing */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DollarSign size={15} style={{ color: 'var(--primary)' }} />
              <span style={S.cardTitle}>Uppgifter & priser</span>
            </div>
            {canManage && project.billing_model !== 'non_billable' && (
              <button
                type="button"
                onClick={() => setShowAddRate(true)}
                style={{ ...S.btnGhost, color: 'var(--primary)', padding: '4px 10px', fontSize: 12 }}
              >
                <Plus size={13} /> Ny uppgift
              </button>
            )}
          </div>
          {rateCardLines.length > 0 ? (
            <table className="proj-table">
              <thead>
                <tr>
                  <th>Uppgift / Artikel</th>
                  <th>Person / Roll</th>
                  <th style={{ textAlign: 'right' }}>Pris</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {rateCardLines.map(line => {
                  const taskLabel = line.article_description || 'Standard';
                  const personLabel = line.employee_firstname
                    ? `${line.employee_firstname} ${line.employee_lastname}`
                    : line.position_title || 'Alla';
                  const canDelete = canManage && rateCardLines.length > 1;
                  const isConfirming = confirmRemoveRate === line.line_id;
                  return (
                    <tr key={line.line_id}>
                      <td><div style={{ fontWeight: 500, color: S.colors.textPrimary }}>{taskLabel}</div></td>
                      <td><span style={{ color: S.colors.textMuted }}>{personLabel}</span></td>
                      <td className="col-right">
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: S.colors.textPrimary }}>
                          {Number(line.billing_rate).toLocaleString('sv-SE')}
                        </span>
                        <span style={{ fontSize: 11, color: S.colors.textLight, marginLeft: 2 }}>{project.currency}/h</span>
                      </td>
                      <td>
                        {canDelete && (
                          isConfirming ? (
                            <div className="proj-inline-confirm proj-inline-confirm-row">
                              <button
                                type="button"
                                onClick={() => handleRemoveRateLine(line.line_id)}
                                className="proj-inline-confirm-yes"
                                title="Bekräfta borttagning"
                              >
                                <Check size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmRemoveRate(null)}
                                className="proj-inline-confirm-no"
                                title="Avbryt"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmRemoveRate(line.line_id)}
                              className="proj-row-trash"
                              title="Ta bort"
                            >
                              <Trash2 size={13} />
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={S.emptyState}>
              <DollarSign size={36} style={S.emptyStateIcon} />
              <div style={S.emptyStateTitle}>
                {project.billing_model === 'non_billable' ? 'Internt projekt' : 'Inget timpris konfigurerat'}
              </div>
              <div style={S.emptyStateDesc}>
                {project.billing_model === 'non_billable'
                  ? 'Interna projekt faktureras inte — ingen prissättning behövs.'
                  : 'Lägg till minst en prisrad så att rapporterad tid kan debiteras.'}
              </div>
              {canManage && project.billing_model !== 'non_billable' && (
                <button type="button" onClick={() => setShowAddRate(true)} style={S.btnPrimary}>
                  <Plus size={14} /> Lägg till första priset
                </button>
              )}
            </div>
          )}
        </div>

        {/* Members */}
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={15} style={{ color: 'var(--primary)' }} />
              <span style={S.cardTitle}>Medlemmar</span>
              <span style={{ fontSize: 11, color: S.colors.textLight }}>{members.length}</span>
            </div>
            {canManage && (
              <button
                type="button"
                onClick={() => setShowAddMember(true)}
                style={{ ...S.btnGhost, color: 'var(--primary)', padding: '4px 10px', fontSize: 12 }}
              >
                <UserPlus size={13} /> Lägg till
              </button>
            )}
          </div>
          {members.length > 0 ? (
            <table className="proj-table">
              <thead>
                <tr>
                  <th>Namn</th>
                  <th>Roll</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => {
                  const isConfirming = confirmRemoveMember === m.member_id;
                  return (
                    <tr key={m.member_id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={S.avatar(30)}>{m.firstname?.[0]}{m.lastname?.[0]}</div>
                          <div>
                            <div style={{ fontWeight: 500, color: S.colors.textPrimary }}>{m.firstname} {m.lastname}</div>
                            <div style={{ fontSize: 11, color: S.colors.textLight }}>{m.jobtitle || m.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={roleBadge[m.project_role] || roleBadge.member}>{roleLabels[m.project_role] || m.project_role}</span>
                      </td>
                      <td>
                        {canManage && (isConfirming ? (
                          <div className="proj-inline-confirm proj-inline-confirm-row">
                            <button
                              type="button"
                              onClick={() => handleRemoveMember(m.member_id)}
                              className="proj-inline-confirm-yes"
                              title="Bekräfta borttagning"
                            >
                              <Check size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmRemoveMember(null)}
                              className="proj-inline-confirm-no"
                              title="Avbryt"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmRemoveMember(m.member_id)}
                            className="proj-row-trash"
                            title="Ta bort"
                          >
                            <Trash2 size={13} />
                          </button>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={S.emptyState}>
              <Users size={36} style={S.emptyStateIcon} />
              <div style={S.emptyStateTitle}>
                {project.require_membership ? 'Inga medlemmar ännu' : 'Öppet projekt'}
              </div>
              <div style={S.emptyStateDesc}>
                {project.require_membership
                  ? 'Lägg till anställda som ska kunna rapportera tid på projektet.'
                  : 'Alla anställda kan rapportera tid. Medlemskap krävs inte.'}
              </div>
              {canManage && project.require_membership && (
                <button type="button" onClick={() => setShowAddMember(true)} style={S.btnPrimary}>
                  <UserPlus size={14} /> Lägg till första medlem
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Rate modal */}
      <Modal show={showAddRate} onHide={() => setShowAddRate(false)} centered>
        <Modal.Header style={{ ...S.modalHeader, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
            <div className="proj-modal-icon">
              <DollarSign size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={S.modalTitle}>Ny uppgift</div>
              <div style={S.modalSubtitle}>
                {project.name}
                {project.billing_model && <> · {S.billingLabel[project.billing_model] || project.billing_model}</>}
                {project.hourly_rate && <> · {Number(project.hourly_rate).toLocaleString('sv-SE')} {project.currency || 'SEK'}/h standard</>}
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body style={S.modalBody}>
          <div style={{ marginBottom: 20 }}>
            <label className="proj-label">Person <span style={{ color: S.colors.textLight, fontWeight: 400 }}>(valfritt)</span></label>
            <select
              className="proj-input proj-select"
              value={newRate.employeeId}
              onChange={e => setNewRate({ ...newRate, employeeId: e.target.value })}
            >
              <option value="">Alla — standardpris</option>
              {members.map(m => <option key={m.employee_id} value={m.employee_id}>{m.firstname} {m.lastname}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label className="proj-label">
              Timpris ({project.currency}/h) <span className="proj-required">*</span>
            </label>
            <input
              type="number"
              min={0}
              step="any"
              placeholder="t.ex. 950"
              value={newRate.billingRate}
              onChange={e => setNewRate({ ...newRate, billingRate: e.target.value })}
              className="proj-input"
            />
          </div>
          <div>
            <label className="proj-label">Faktura-artikel <span style={{ color: S.colors.textLight, fontWeight: 400 }}>(valfritt)</span></label>
            <CreatableSelect
              options={articles.map(a => ({ value: a.article_id || a.id, label: a.description || a.productcode }))}
              value={newRate.articleId ? { value: newRate.articleId, label: articles.find(a => (a.article_id || a.id) === newRate.articleId)?.description || '' } : newRate.newArticleName ? { value: '__new__', label: newRate.newArticleName } : null}
              onChange={opt => {
                if (!opt) setNewRate({ ...newRate, articleId: '', newArticleName: '' });
                else if (opt.__isNew__) setNewRate({ ...newRate, articleId: '', newArticleName: opt.label });
                else setNewRate({ ...newRate, articleId: opt.value, newArticleName: '' });
              }}
              isClearable
              isSearchable
              placeholder="Sök befintlig eller skriv för att skapa ny..."
              formatCreateLabel={v => `Skapa ny artikel: "${v}"`}
              noOptionsMessage={() => 'Skriv ett namn för att skapa ny'}
              styles={S.selectStyles}
            />
            <div className="proj-helper">
              Lämna tomt för standardartikel. Skriv nytt namn för att skapa en kontraktsspecifik artikel.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={S.modalFooter}>
          <button type="button" onClick={() => setShowAddRate(false)} style={S.btnSecondary}>Avbryt</button>
          <button
            type="button"
            onClick={handleAddRateLine}
            disabled={!newRate.billingRate}
            style={{ ...S.btnPrimary, opacity: newRate.billingRate ? 1 : 0.5 }}
          >
            <Plus size={14} /> Lägg till uppgift
          </button>
        </Modal.Footer>
      </Modal>

      {/* Add Member modal */}
      <Modal show={showAddMember} onHide={() => setShowAddMember(false)} centered>
        <Modal.Header style={{ ...S.modalHeader, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
            <div className="proj-modal-icon">
              <UserPlus size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={S.modalTitle}>Lägg till medlem</div>
              <div style={S.modalSubtitle}>
                {project.name} · {members.length} {members.length === 1 ? 'befintlig medlem' : 'befintliga medlemmar'}
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body style={S.modalBody}>
          <div style={{ marginBottom: 20 }}>
            <label className="proj-label">Anställd <span className="proj-required">*</span></label>
            <Select
              options={availableEmployees.map(e => ({
                value: e.employee_id || e.id,
                label: `${e.name || `${e.firstname} ${e.lastname}`}${e.jobtitle ? ` — ${e.jobtitle}` : ''}`,
              }))}
              onChange={opt => setNewMember({ ...newMember, employeeId: opt?.value || '' })}
              isClearable
              isSearchable
              placeholder="Sök efter namn..."
              noOptionsMessage={() =>
                availableEmployees.length === 0 && employees.length > 0
                  ? 'Alla anställda är redan medlemmar'
                  : 'Inga anställda hittades'
              }
              styles={S.selectStyles}
            />
          </div>
          <div>
            <label className="proj-label">Roll i projektet</label>
            <select
              className="proj-input proj-select"
              value={newMember.projectRole}
              onChange={e => setNewMember({ ...newMember, projectRole: e.target.value })}
            >
              <option value="member">Medlem — kan rapportera tid</option>
              <option value="lead">Ledare — kan godkänna tid</option>
              <option value="manager">Ansvarig — full kontroll</option>
              <option value="viewer">Läsare — kan bara se</option>
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer style={S.modalFooter}>
          <button type="button" onClick={() => setShowAddMember(false)} style={S.btnSecondary}>Avbryt</button>
          <button
            type="button"
            onClick={handleAddMember}
            disabled={!newMember.employeeId}
            style={{ ...S.btnPrimary, opacity: newMember.employeeId ? 1 : 0.5 }}
          >
            <UserPlus size={14} /> Lägg till medlem
          </button>
        </Modal.Footer>
      </Modal>

      <style>{projDetailCss}</style>
    </>
  );
};

// Scoped CSS — extracted to a const because it's substantial. Matches the
// `.proj-*` language established on ProjectEditPage and ProjectReportsPage.
const projDetailCss = `
  /* Toolbar */
  .proj-toolbar {
    display: flex; align-items: center; gap: 12px; margin-bottom: 12px;
    padding: 10px 14px; background: white;
    border: 1px solid ${S.colors.borderDefault}; border-radius: 10px;
    flex-wrap: wrap;
  }
  .proj-toolbar-right { display: flex; align-items: center; gap: 8px; margin-left: auto; }
  .proj-detail-toolbar { position: sticky; top: 0; z-index: 20; }
  .proj-detail-titleblock { display: flex; flex-direction: column; min-width: 0; gap: 4px; }
  .proj-detail-title-row {
    display: flex; align-items: center; gap: 8px; min-width: 0;
  }
  .proj-color-dot {
    width: 10px; height: 10px; border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.1); flex-shrink: 0;
  }
  .proj-detail-title {
    font-size: 15px; font-weight: 700; color: ${S.colors.textPrimary};
    letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .proj-detail-code {
    font-size: 11px; color: ${S.colors.textLight};
    background: ${S.colors.bgHover}; padding: 2px 8px; border-radius: 4px;
    font-variant-numeric: tabular-nums;
  }
  .proj-detail-sub {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; color: ${S.colors.textMuted};
  }
  .proj-detail-sep { color: ${S.colors.borderDefault}; }

  /* Inline confirmations replace swal */
  .proj-inline-confirm {
    display: inline-flex; align-items: center; gap: 4px;
    background: ${S.colors.dangerLight}; border-radius: 8px;
    padding: 4px 8px;
    animation: proj-pop 120ms ease-out;
  }
  @keyframes proj-pop { from { transform: scale(0.94); opacity: 0; } to { transform: scale(1); opacity: 1; } }
  .proj-inline-confirm-label { font-size: 12px; font-weight: 600; color: ${S.colors.danger}; padding: 0 4px; }
  .proj-inline-confirm-yes {
    width: 26px; height: 26px; border-radius: 6px;
    border: none; background: ${S.colors.danger}; color: white; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    transition: background 120ms ease;
  }
  .proj-inline-confirm-yes:hover:not(:disabled) { background: #b91c1c; }
  .proj-inline-confirm-yes:disabled { opacity: 0.7; cursor: not-allowed; }
  .proj-inline-confirm-no {
    width: 26px; height: 26px; border-radius: 6px;
    border: 1px solid ${S.colors.borderDefault}; background: white; color: ${S.colors.textMuted}; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    transition: background 120ms ease;
  }
  .proj-inline-confirm-no:hover:not(:disabled) { background: ${S.colors.bgHover}; }
  .proj-inline-confirm-row { padding: 2px 4px; gap: 2px; }
  .proj-inline-confirm-row .proj-inline-confirm-yes,
  .proj-inline-confirm-row .proj-inline-confirm-no { width: 22px; height: 22px; border-radius: 5px; }

  /* Row trash button */
  .proj-row-trash {
    background: none; border: none; color: ${S.colors.textDisabled};
    cursor: pointer; padding: 4px; border-radius: 4px;
    display: inline-flex; align-items: center; justify-content: center;
    transition: color 120ms ease, background 120ms ease;
  }
  .proj-row-trash:hover { color: ${S.colors.danger}; background: ${S.colors.dangerLight}; }

  /* Setup banner */
  .proj-setup-banner {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
    padding: 12px 16px; margin-bottom: 12px;
    background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px;
  }
  .proj-setup-banner-title {
    font-size: 13px; font-weight: 600; color: #92400e; margin-bottom: 2px;
  }
  .proj-setup-banner-desc { font-size: 13px; color: #78350f; line-height: 1.5; }
  .proj-setup-banner-desc b { font-weight: 600; }
  .proj-setup-skip {
    background: transparent; border: none; color: #92400e;
    font-size: 12px; font-weight: 500; cursor: pointer; padding: 4px 8px; white-space: nowrap;
  }
  .proj-setup-skip:hover { text-decoration: underline; }

  /* Description + period meta line */
  .proj-detail-meta {
    display: flex; flex-wrap: wrap; gap: 16px;
    padding: 8px 2px; margin-bottom: 12px;
    font-size: 12px; color: ${S.colors.textMuted};
  }
  .proj-detail-desc { flex: 1; min-width: 280px; line-height: 1.5; }
  .proj-detail-period {
    display: inline-flex; align-items: center; gap: 6px;
    color: ${S.colors.textLight};
  }

  /* Stats strip — shared with ProjectReportsPage */
  .proj-stats-strip {
    display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
    padding: 10px 14px;
    background: ${S.colors.bgSubtle}; border: 1px solid ${S.colors.borderDefault}; border-radius: 8px;
    font-size: 13px; color: ${S.colors.textSecondary};
  }
  .proj-stat { display: inline-flex; align-items: baseline; gap: 6px; }
  .proj-stat-label { color: ${S.colors.textLight}; font-size: 12px; }
  .proj-stat b { font-weight: 700; }
  .proj-stat-sub { color: ${S.colors.textLight}; font-size: 11px; margin-left: 4px; }
  .proj-stat-sep { color: ${S.colors.borderDefault}; }

  /* Budget bar — page-wide signal under the strip */
  .proj-budget-bar {
    position: relative;
    width: 100%; height: 6px;
    background: ${S.colors.borderLight}; border-radius: 3px; overflow: visible;
  }
  .proj-budget-bar-fill {
    height: 100%; border-radius: 3px;
    transition: width 400ms ease, background-color 200ms ease;
  }
  .proj-budget-bar-overrun {
    position: absolute; top: -1px; right: 0; height: 8px;
    background: ${S.colors.danger}; border-radius: 0 4px 4px 0;
    box-shadow: 0 0 0 1px white;
  }

  /* Disclosure toggle for charts */
  .proj-disclosure {
    display: inline-flex; align-items: center; gap: 6px;
    background: white; border: 1px solid ${S.colors.borderDefault};
    border-radius: 8px; padding: 6px 12px;
    font-size: 12px; font-weight: 500; color: ${S.colors.textSecondary};
    cursor: pointer; transition: background 120ms ease;
  }
  .proj-disclosure:hover { background: ${S.colors.bgHover}; }

  /* Chart layout */
  .proj-charts-donuts {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  }
  .proj-donut-legend {
    display: flex; flex-wrap: wrap; justify-content: center;
    gap: 6px 14px; margin-top: 8px; padding: 0 4px;
  }
  .proj-donut-legend-item {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; color: ${S.colors.textMuted};
  }

  /* Content split */
  .proj-detail-split {
    display: grid; grid-template-columns: 7fr 5fr; gap: 16px; margin-bottom: 16px;
  }
  @media (max-width: 1100px) {
    .proj-detail-split { grid-template-columns: 1fr; }
    .proj-charts-donuts { grid-template-columns: 1fr; }
  }

  /* Tables */
  .proj-table {
    width: 100%; border-collapse: collapse; font-size: 13px;
  }
  .proj-table th {
    padding: 10px 20px; font-size: 11px; font-weight: 500;
    color: ${S.colors.textLight}; text-transform: uppercase; letter-spacing: 0.03em;
    text-align: left; background: ${S.colors.bgSubtle};
    border-bottom: 1px solid ${S.colors.borderLight};
  }
  .proj-table td {
    padding: 12px 20px; border-bottom: 1px solid ${S.colors.borderLight};
    vertical-align: middle;
  }
  .proj-table tr:last-child td { border-bottom: none; }
  .proj-table tr:hover td { background: ${S.colors.bgSubtle}; }
  .proj-table .col-right { text-align: right; }

  /* Form primitives — shared with ProjectEditPage */
  .proj-label {
    display: block; margin-bottom: 6px;
    font-size: 12px; font-weight: 500; color: ${S.colors.textLight};
  }
  .proj-required { color: ${S.colors.danger}; font-weight: 600; }
  .proj-helper {
    font-size: 11px; color: ${S.colors.textLight}; margin-top: 6px; line-height: 1.4;
  }
  .proj-input {
    width: 100%;
    border-radius: 8px;
    border: 1px solid ${S.colors.borderDefault};
    font-size: 14px; height: 40px; padding: 0 12px;
    background: white; color: ${S.colors.textPrimary};
    transition: border-color 150ms ease, box-shadow 150ms ease;
    outline: none; font-family: inherit;
  }
  .proj-input:hover { border-color: #d1d5db; }
  .proj-input:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(68,129,78,0.12);
  }
  .proj-select {
    appearance: none; -webkit-appearance: none;
    padding-right: 32px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
    background-repeat: no-repeat;
    background-position: right 12px center;
  }

  /* Modal icon chip — shared with modals */
  .proj-modal-icon {
    width: 40px; height: 40px; border-radius: 8px;
    background: var(--rgba-primary-1); color: var(--bs-primary);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  /* Save-button spinner reused from ProjectEditPage */
  @keyframes proj-spin { to { transform: rotate(360deg); } }
  .proj-spin { animation: proj-spin 0.8s linear infinite; }
`;

export default ProjectDetailsPage;
